import React, { useState } from "react";
import { Button, Input } from "antd";
import { CheckCircleFilled, LockOutlined, PhoneOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useUser from "../../hooks/useUser";
import Sheet from "./Sheet";

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

// Member login, with the forgot-password (OTP) flow in the same sheet.
// step: "login" | "forgot-phone" | "forgot-otp" | "forgot-done"
const LoginSheet = ({ open, onClose, setEndUser }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userSignin, sendOtp, resetPasswordWithOtp } = useUser();
  const [step, setStep] = useState("login");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const close = () => {
    onClose();
    setStep("login");
    setPassword("");
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const { mutate: login, isPending: loggingIn } = useMutation({
    mutationFn: userSignin,
    onSuccess: (res) => {
      localStorage.setItem("user-token", res.token);
      setEndUser(res);
      queryClient.invalidateQueries();
      toast.success(`Welcome back${res?.name ? `, ${res.name}` : ""}!`);
      close();
    },
    onError: (err) => toast.error(errorMessage(err, "Login failed")),
  });

  const { mutate: requestOtp, isPending: sendingOtp } = useMutation({
    mutationFn: sendOtp,
    onSuccess: () => {
      toast.success("OTP sent to your phone");
      setStep("forgot-otp");
    },
    onError: (err) => toast.error(errorMessage(err, "Failed to send OTP")),
  });

  const { mutate: resetPassword, isPending: resetting } = useMutation({
    mutationFn: resetPasswordWithOtp,
    onSuccess: () => setStep("forgot-done"),
    onError: (err) => toast.error(errorMessage(err, "Reset failed. Check your OTP.")),
  });

  const submitLogin = (e) => {
    e.preventDefault();
    if (!phone) return toast.error("Phone number required");
    if (!password) return toast.error("Password required");
    login({ phone, password });
  };

  const submitPhone = (e) => {
    e.preventDefault();
    if (!phone) return toast.error("Enter your phone number");
    requestOtp({ phone });
  };

  const submitReset = (e) => {
    e.preventDefault();
    if (!otp) return toast.error("Enter the OTP");
    if (!newPassword) return toast.error("Enter a new password");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match");
    resetPassword({ phone, otp, password: newPassword });
  };

  const phoneInput = (
    <Input
      size="large"
      prefix={<PhoneOutlined />}
      placeholder="Registered phone number"
      inputMode="tel"
      autoComplete="username"
      value={phone}
      onChange={(e) => setPhone(e.target.value)}
    />
  );

  return (
    <Sheet open={open} onClose={close}>
      {step === "login" && (
        <form className="sheet-form" onSubmit={submitLogin}>
          <h2 className="sheet-form__title">Member login</h2>
          <p className="sheet-form__hint">Use the phone number and password you registered with.</p>
          {phoneInput}
          <Input.Password
            size="large"
            prefix={<LockOutlined />}
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="primary" htmlType="submit" size="large" block loading={loggingIn}>
            Log in
          </Button>
          <div className="sheet-form__links">
            <button type="button" onClick={() => setStep("forgot-phone")}>
              Forgot password?
            </button>
            <button
              type="button"
              onClick={() => {
                close();
                navigate("/app-registration");
              }}
            >
              Create an account
            </button>
          </div>
        </form>
      )}

      {step === "forgot-phone" && (
        <form className="sheet-form" onSubmit={submitPhone}>
          <h2 className="sheet-form__title">Reset password</h2>
          <p className="sheet-form__hint">We'll send a one-time code to your registered phone.</p>
          {phoneInput}
          <Button type="primary" htmlType="submit" size="large" block loading={sendingOtp}>
            Send OTP
          </Button>
          <div className="sheet-form__links">
            <button type="button" onClick={() => setStep("login")}>
              Back to login
            </button>
          </div>
        </form>
      )}

      {step === "forgot-otp" && (
        <form className="sheet-form" onSubmit={submitReset}>
          <h2 className="sheet-form__title">Set a new password</h2>
          <p className="sheet-form__hint">
            Enter the code sent to <strong>{phone}</strong>.
          </p>
          <Input
            size="large"
            placeholder="OTP"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
          <Input.Password
            size="large"
            prefix={<LockOutlined />}
            placeholder="New password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <Input.Password
            size="large"
            prefix={<LockOutlined />}
            placeholder="Confirm new password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <Button type="primary" htmlType="submit" size="large" block loading={resetting}>
            Reset password
          </Button>
          <div className="sheet-form__links">
            <button type="button" onClick={() => setStep("forgot-phone")}>
              Change phone number
            </button>
          </div>
        </form>
      )}

      {step === "forgot-done" && (
        <div className="sheet-form sheet-form--center">
          <CheckCircleFilled className="sheet-form__done-icon" />
          <h2 className="sheet-form__title">Password updated</h2>
          <p className="sheet-form__hint">You can now log in with your new password.</p>
          <Button type="primary" size="large" block onClick={() => setStep("login")}>
            Go to login
          </Button>
        </div>
      )}
    </Sheet>
  );
};

export default LoginSheet;
